import { useEffect, useState } from "react";
import { ArrowRightLeft, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fetchRates, fetchHistory } from "../lib/api";
import { useT } from "../i18n/I18nProvider";

const CURRENCIES = ["EUR", "USD", "CHF", "GBP", "CAD"];

export default function CurrencyConverter() {
  const { t } = useT();
  const [rates, setRates] = useState(null);
  const [history, setHistory] = useState([]);
  const [from, setFrom] = useState("PLN");
  const [to, setTo] = useState("EUR");
  const [amount, setAmount] = useState(1000);
  const [effectiveDate, setEffectiveDate] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchRates();
        setRates(r.rates);
        const first = Object.values(r.rates)[0];
        if (first) setEffectiveDate(first.effective_date);
      } catch { /* ignored */ }
    })();
  }, []);

  useEffect(() => {
    const cur = from === "PLN" ? to : from;
    if (cur === "PLN") return;
    fetchHistory(cur, 30).then((d) => setHistory(d.data || [])).catch(() => {});
  }, [from, to]);

  const convert = () => {
    if (!rates) return 0;
    if (from === "PLN" && to === "PLN") return amount;
    if (from === "PLN") { const r = rates[to]?.rate; return r ? amount / r : 0; }
    if (to === "PLN") { const r = rates[from]?.rate; return r ? amount * r : 0; }
    const rFrom = rates[from]?.rate;
    const rTo = rates[to]?.rate;
    if (!rFrom || !rTo) return 0;
    return (amount * rFrom) / rTo;
  };

  const swap = () => { const a = from; setFrom(to); setTo(a); };
  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid="currency-converter">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display font-semibold text-xl text-slate-900">{t("currency.title")}</h3>
          <p className="text-sm text-slate-500 mt-0.5">{t("currency.sub")}</p>
        </div>
        <TrendingUp className="w-5 h-5 text-[#0056D2]" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">{t("currency.amount")}</label>
          <div className="flex gap-2">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value) || 0)}
              className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#0056D2] focus:border-transparent outline-none"
              data-testid="converter-amount"
            />
            <select value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-xl bg-white font-medium" data-testid="converter-from">
              <option value="PLN">PLN</option>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <button onClick={swap} className="p-3 rounded-full bg-slate-100 hover:bg-slate-200 transition self-end mb-0.5" data-testid="converter-swap" aria-label={t("currency.swap")}>
          <ArrowRightLeft className="w-4 h-4 text-slate-600" />
        </button>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">{t("currency.to")}</label>
          <div className="flex gap-2">
            <div className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl bg-slate-50 font-semibold text-slate-900 tabular-nums" data-testid="converter-result">
              {convert().toLocaleString("fr-FR", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
            </div>
            <select value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-xl bg-white font-medium" data-testid="converter-to">
              <option value="PLN">PLN</option>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {effectiveDate && <p className="text-xs text-slate-500 mt-3" data-testid="converter-effective-date">{t("currency.nbpDate")} {formatDate(effectiveDate)}</p>}

      {history.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="flex justify-between items-baseline mb-3">
            <h4 className="font-display font-medium text-slate-900">{t("currency.historyTitle")}</h4>
            <span className="text-xs text-slate-500">PLN / {from === "PLN" ? to : from}</span>
          </div>
          <div className="h-32" data-testid="currency-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" hide />
                <YAxis domain={["auto", "auto"]} fontSize={10} tick={{ fill: "#94a3b8" }} width={40} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => [v.toFixed(4) + " PLN", "Taux"]}
                  labelFormatter={(d) => new Date(d).toLocaleDateString("fr-FR")}
                />
                <Line type="monotone" dataKey="rate" stroke="#0056D2" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
